import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { subscribeWithSelector } from 'zustand/middleware';
import dayjs from 'dayjs';
import { generateName } from '../utils/string';
import { WidgetType } from '../widgets/widgets';

export type WidgetConfig = {
    verticalPadding: number;
    horizontalPadding: number;
    borderRadius: number;
    borderWidth: number;
    borderColor: string;
    backgroundColor: string;
    titleText: string;
    timerType: 'fixed_date' | 'fixed_minutes';
    fixedDate?: string;
    fixedMinutes?: number;
    endText: string;
    onFinish: 'hide' | 'show_text' | 'do_nothing';
    placements: {
        page: 'home' | 'collections' | 'product' | 'collection' | 'cart' | 'search' | 'checkout' | 'contact';
        position: 'top' | 'top_fixed' | 'bottom_fixed' | 'add_to_cart';
        hide_on_hover: boolean;
    }[];
    name: string;
    _id?: string;
    createdAt?: string;
    updatedAt?: string;
    shop?: string;
    active: boolean;
    views?: number;
    shownAt?: string;
    fullWidth: boolean;
    resumeSessionTimer: boolean;
    freeShippingThreshold?: number;
    topMargin: number;
    bottomMargin: number;
    inventoryStartThreshold?: number;
    widgetType: WidgetType;
    scrollingText: boolean;
    gradientEnabled: boolean;
    gradientColor: string;
    gradientDirection: 'to right' | 'to left' | 'to bottom' | 'to top' | '135deg' | '45deg';
    shadowEnabled: boolean;
    shadowIntensity: 'light' | 'medium' | 'heavy';
    animationStyle: 'none' | 'shimmer' | 'glow' | 'pulse-border' | 'gradient-shift';
};

type Actions = {
    setVerticalPadding: (value: number) => void;
    setHorizontalPadding: (value: number) => void;
    setBorderRadius: (value: number) => void;
    setBorderWidth: (value: number) => void;
    setBorderColor: (value: string) => void;
    setBackgroundColor: (value: string) => void;
    setTitleText: (value: string) => void;
    setTimerType: (value: WidgetConfig['timerType']) => void;
    setFixedDate: (value: string) => void;
    setFixedMinutes: (value: number) => void;
    setEndText: (value: string) => void;
    setOnFinish: (value: WidgetConfig['onFinish']) => void;
    setPlacements: (value: WidgetConfig['placements']) => void;
    setName: (value: string) => void;
    getConfig: () => WidgetConfig;
    setWidget: (value: WidgetConfig) => void;
    setId: (value: string) => void;
    resetState: () => void;
    cloneWidget: () => void;
    setActive: (value: boolean) => void;
    setFullWidth: (value: boolean) => void;
    setResumeSessionTimer: (value: boolean) => void;
    setFreeShippingThreshold: (value: number) => void;
    setTopMargin: (value: number) => void;
    setBottomMargin: (value: number) => void;
    setInventoryStartThreshold: (value: number) => void;
    setWidgetType: (value: WidgetType) => void;
    setScrollingText: (value: boolean) => void;
    setGradientEnabled: (value: boolean) => void;
    setGradientColor: (value: string) => void;
    setGradientDirection: (value: WidgetConfig['gradientDirection']) => void;
    setShadowEnabled: (value: boolean) => void;
    setShadowIntensity: (value: WidgetConfig['shadowIntensity']) => void;
    setAnimationStyle: (value: WidgetConfig['animationStyle']) => void;
};

const initialState: WidgetConfig = {
    verticalPadding: 30,
    horizontalPadding: 150,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#74A6E0',
    backgroundColor: '#BFE5F3',
    titleText:
        '<p style="text-align: center;"><span style="font-size: 26px; font-family: Roboto;">Start editing to customize this widget</span></p>',
    timerType: 'fixed_minutes',
    fixedDate: dayjs().add(1, 'day').format('YYYY-MM-DDTHH:mm'),
    fixedMinutes: 240,
    endText: '',
    onFinish: 'do_nothing',
    placements: [{ page: 'home', position: 'top', hide_on_hover: false }],
    name: generateName(),
    active: false,
    _id: undefined,
    createdAt: undefined,
    updatedAt: undefined,
    views: 0,
    shownAt: undefined,
    fullWidth: false,
    resumeSessionTimer: false,
    topMargin: 0,
    bottomMargin: 0,
    inventoryStartThreshold: 100,
    widgetType: WidgetType.DEFAULT,
    scrollingText: false,
    gradientEnabled: false,
    gradientColor: '#FF8C00',
    gradientDirection: 'to right' as const,
    shadowEnabled: true,
    shadowIntensity: 'medium' as const,
    animationStyle: 'none' as const,
};

export const useWidgetStore = create<WidgetConfig & Actions>()(
    subscribeWithSelector(
        immer((set, get) => ({
            ...initialState,

            setVerticalPadding: value => {
                set(state => {
                    state.verticalPadding = value;
                });
            },
            setHorizontalPadding: value => {
                set(state => {
                    state.horizontalPadding = value;
                });
            },
            setBorderRadius: value => {
                set(state => {
                    state.borderRadius = value;
                });
            },
            setBorderWidth: value => {
                set(state => {
                    state.borderWidth = value;
                });
            },
            setBorderColor: value => {
                set(state => {
                    state.borderColor = value;
                });
            },
            setBackgroundColor: value => {
                set(state => {
                    state.backgroundColor = value;
                });
            },
            setTitleText: value => {
                set(state => {
                    state.titleText = value;
                });
            },
            setTimerType: value => {
                set(state => {
                    state.timerType = value;
                });
            },
            setFixedDate: value => {
                set(state => {
                    state.fixedDate = value;
                });
            },
            setFixedMinutes: value => {
                set(state => {
                    state.fixedMinutes = value;
                });
            },
            setEndText: value => {
                set(state => {
                    state.endText = value;
                });
            },
            setOnFinish: value => {
                set(state => {
                    state.onFinish = value;
                });
            },
            setPlacements: value => {
                set(state => {
                    state.placements = value;
                });
            },
            setName: value => {
                set(state => {
                    state.name = value;
                });
            },
            getConfig: () => {
                return get();
            },
            setWidget: value => {
                set(() => ({ ...initialState, ...value }));
            },
            setId: value => {
                set(state => {
                    state._id = value;
                });
            },
            resetState: () => {
                set(() => initialState);
            },
            cloneWidget: () => {
                set(state => {
                    state._id = undefined;
                    state.name = generateName();
                    state.createdAt = undefined;
                    state.updatedAt = undefined;
                    state.active = false;
                });
            },
            setActive: value => {
                set(state => {
                    state.active = value;
                });
            },
            setFullWidth: value => {
                set(state => {
                    state.fullWidth = value;
                });
            },
            setResumeSessionTimer: value => {
                set(state => {
                    state.resumeSessionTimer = value;
                });
            },
            setFreeShippingThreshold: value => {
                set(state => {
                    state.freeShippingThreshold = value;
                });
            },
            setTopMargin: value => {
                set(state => {
                    state.topMargin = value;
                });
            },
            setBottomMargin: value => {
                set(state => {
                    state.bottomMargin = value;
                });
            },
            setInventoryStartThreshold: value => {
                set(state => {
                    state.inventoryStartThreshold = value;
                });
            },
            setWidgetType: value => {
                set(state => {
                    state.widgetType = value;
                });
            },
            setScrollingText: value => {
                set(state => {
                    state.scrollingText = value;
                });
            },
            setGradientEnabled: value => {
                set(state => {
                    state.gradientEnabled = value;
                });
            },
            setGradientColor: value => {
                set(state => {
                    state.gradientColor = value;
                });
            },
            setGradientDirection: value => {
                set(state => {
                    state.gradientDirection = value;
                });
            },
            setShadowEnabled: value => {
                set(state => {
                    state.shadowEnabled = value;
                });
            },
            setShadowIntensity: value => {
                set(state => {
                    state.shadowIntensity = value;
                });
            },
            setAnimationStyle: value => {
                set(state => {
                    state.animationStyle = value;
                });
            },
        }))
    )
);
